import { useTranslation } from "react-i18next";
import React, { useCallback, useRef, useState } from "react";
import {
  CameraViewer,
  CameraViews,
  DroneControlPanel,
  NotificationInfo,
  SetCamerasModelDTO,
  SignalRProvider,
  useIsMobileDetect,
} from "@shared/ui";
import { useParams } from "react-router-dom";
import "./CameraView.css";
import { Rnd } from "react-rnd";
import Recorder from "@shared/ui/dist/components/Recorder/Recorder";
import { NotificationInfoType } from "@shared/ui/dist/components/NotoficationInfo/interfaces";

const CameraView: React.FC<{ isViewOnly: boolean }> = ({ isViewOnly }) => {
  const { t } = useTranslation();
  const [view, setView] = useState(1);
  const { droneId } = useParams();
  const [disconnect, setDisconnect] = useState(false);
  const [cameras, setCameras] = useState<SetCamerasModelDTO[]>([]);
  const [isNotificationInfo, setIsNotificationInfo] = useState(() => {
    if (localStorage.getItem("notificationInfoClosed") === null) {
      localStorage.setItem("notificationInfoClosed", "true");
    }
    return localStorage.getItem("notificationInfoClosed") === "false";
  });
  const [muteMic, setMuteMic] = useState(true);
  const [muteAudio, setMuteAudio] = useState(true);
  const [micVolume, setMicVolume] = useState<number>(0.3);
  const [cameraStreams, setCameraStreams] = useState<
    Map<string, { stream: MediaStream | null; loaded: boolean }>
  >(new Map());

  const [streams, setStreams] = useState<{
    front: MediaStream | null;
    back: MediaStream | null;
  }>({ front: null, back: null });

  const camerasRef = useRef<SetCamerasModelDTO[]>([]);
  camerasRef.current = cameras;

  const isMobilePortrait = useIsMobileDetect();

  // Single callback for all stream state changes from CameraViews.
  // Also maps camera index → front/back for the Recorder.
  const handleCameraStreamUpdate = useCallback(
    (name: string, stream: MediaStream | null, loaded: boolean) => {
      setCameraStreams((prev) => {
        const next = new Map(prev);
        next.set(name, { stream, loaded });
        return next;
      });
      const idx = camerasRef.current.findIndex((c) => c.name === name);
      if (idx === 0)
        setStreams((s) => ({ ...s, front: loaded ? stream : null }));
      else if (idx === 1)
        setStreams((s) => ({ ...s, back: loaded ? stream : null }));
    },
    [],
  );

  const droneConnected = async (data: SetCamerasModelDTO[]) => {
    setCameras((prev) => {
      if (prev.length === 0) {
        return data;
      }
      return prev;
    });
  };

  return (
    <SignalRProvider hub={`droneHub?token=${droneId}&clientType=1`}>
      {isNotificationInfo && (
        <NotificationInfo
          type={NotificationInfoType.Update}
          setIsNotificationInfo={setIsNotificationInfo}
        />
      )}
      <div className={!isViewOnly ? "wrapper controls" : "wrapper no-controls"}>
        <div className="switch-view-camera">
          {cameras.length > 0 ? (
            <button
              onClick={() => setView(1)}
              className={view === 1 ? "camera-view1 active" : "camera-view1"}
            >
              View 1
            </button>
          ) : null}
          {cameras.length >= 2 ? (
            <button
              onClick={() => setView(2)}
              className={view === 2 ? "camera-view2 active" : "camera-view2"}
            >
              View 2
            </button>
          ) : null}
          {cameras.length >= 2 ? (
            <button
              onClick={() => setView(3)}
              className={view === 3 ? "camera-view3 active" : "camera-view3"}
            >
              View 4
            </button>
          ) : null}
        </div>
        {droneId && cameras.length ? (
          <CameraViews
            cameras={cameras}
            view={view}
            droneId={droneId!}
            disconnect={disconnect}
            muteMic={muteMic}
            muteAudio={muteAudio}
            micVolume={micVolume}
            onStreamUpdate={handleCameraStreamUpdate}
            isViewOnly={isViewOnly}
          />
        ) : null}

        {!isMobilePortrait && view === 1 && cameras.length > 1 && (
          <Rnd
            default={{
              x: 0,
              y: window.innerHeight - 55 - 168,
              width: 300,
              height: 168,
            }}
            style={{
              zIndex: "110000",
            }}
          >
            <div className="small-camera-view">
              <CameraViewer
                camera={cameras[1]}
                stream={cameraStreams.get(cameras[1].name)?.stream ?? null}
                isCameraLoaded={
                  cameraStreams.get(cameras[1].name)?.loaded ?? false
                }
                smallView={true}
                usage="back"
              />
            </div>
          </Rnd>
        )}

        <div
          className="rotate-overlay"
          aria-live="polite"
          role="dialog"
          aria-modal="true"
        >
          <div className="rotate-card">
            <svg className="rotate-icon" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M7 7h10a2 2 0 0 1 2 2v5.5a.5.5 0 0 1-1 0V9a1 1 0 0 0-1-1H7a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h6.5a.5.5 0 0 1 0 1H7a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2z" />
              <path d="M20.5 15a.5.5 0 0 1 .5.5 6.5 6.5 0 1 1-11.09-4.61.5.5 0 0 1 .68.73A5.5 5.5 0 1 0 21 15.5a.5.5 0 0 1-.5.5H18a.5.5 0 0 1 0-1h2.5z" />
            </svg>
            <p className="rotate-title">{t("cameraView.rotateTitle")}</p>
            <p className="rotate-text">{t("cameraView.rotateText")}</p>
          </div>
        </div>

        {droneId && (
          <DroneControlPanel
            onDroneConnected={droneConnected}
            droneId={droneId}
            cameraView={view}
            isViewOnly={isViewOnly}
            setMuteMic={setMuteMic}
            setMuteAudio={setMuteAudio}
            setMicVolume={setMicVolume}
          />
        )}

        {!isMobilePortrait && (streams.back || streams.front) && <Recorder streams={streams} />}
      </div>
    </SignalRProvider>
  );
};
export default CameraView;
